import { useEffect, useState } from 'react';
import useClient from 'api/client';
import { useAlert } from 'components/message/AlertContext';
import {
    BlockPreviewOptions,
    Environment,
    ModifyEnvironmentInput,
} from 'types/Environment';
import updateEnvironment from 'api/Environment/updateEnvironment';
import { useParams } from 'react-router-dom';
import { StackType } from 'views/Stack/StackDetails';
import useStack from 'views/Stack/hooks/useStack';
import queryEditableFields from 'api/Environment/queryEditableFields';
import { isValidCombinedLength } from './ValidateLabelAndGroups';

export default function useUpdateEnvironment() {
    const client = useClient();
    const params = useParams<{ uri: string }>();
    const { updateStack } = useStack(StackType.environment);
    const { openAlert } = useAlert();
    const [updating, setUpdating] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [environment, setEnvironment] = useState<Environment>({
        description: '',
        environmentUri: '',
        created: '',
        label: '',
        userRoleInEnvironment: '',
        originSamlGroupName: [],
        originSamlFullDevGroupName: [],
        originSamlReaderGroupName: [],
        blockpreview: BlockPreviewOptions.All,
        stack: {
            status: '',
        },
        quicksight_enabled: false,
        role_overwrite_enabled: false,
    });
    useEffect(() => {
        const fetchData = async () => {
            try {
                if (params.uri) {
                    setLoading(true);
                    const response = await client?.query(
                        queryEditableFields(params.uri),
                    );
                    if (!response.errors) {
                        setEnvironment(response.data.getEnvironment);
                    } else {
                        setError(true);
                        openAlert({
                            message:
                                'Something went wrong... Could not retrieve dataset',
                            severity: 'alert',
                        });
                    }
                }
            } finally {
                setLoading(false);
            }
        };

        if (client) {
            fetchData();
        }
    }, [client, params.uri]);

    const validateForm = (formData: ModifyEnvironmentInput) => {
        const validations = [
            isValidCombinedLength(
                46,
                environment.label,
                formData.SamlGroupName,
            ),
            isValidCombinedLength(
                46,
                environment.label,
                formData.SamlFullDevGroupName,
            ),
            isValidCombinedLength(
                46,
                environment.label,
                formData.SamlReaderGroupName,
            ),
        ];

        return validations.every((validation) => validation);
    };

    const handleSubmit = async (formData: ModifyEnvironmentInput) => {
        setUpdating(true);
        try {
            if (params.uri) {
                const validForm = validateForm(formData);

                if (!validForm) {
                    openAlert({
                        message: `The combined length of the label and the group names should not exceed 46 characters`,
                        severity: 'alert',
                    });
                    setUpdating(false);
                    return;
                }

                const response = await client.mutate(
                    updateEnvironment(params.uri, formData),
                );

                if (!response.errors) {
                    openAlert({
                        message: `Succesfully updated environment`,
                        severity: 'success',
                    });
                    updateStack();
                } else {
                    openAlert({
                        message: `Could not udpate environment, received ${response.errors[0].message}`,
                        severity: 'alert',
                    });
                }
            }
        } finally {
            setUpdating(false);
        }
    };

    return {
        environment,
        loading,
        error,
        handleSubmit,
        updating,
    };
}
